import { isEqual } from 'lodash-es'
import { computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs'
import type { GetCurrentMemberFn } from '@/features/iam'
import type { AlertRetryWrapperFactory } from '@/features/shell'
import type { ActivationLifecycle } from '@/infra/activation'
import { Id } from '@/infra/id'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ViewModelList } from '@/infra/view-model-list'
import { toStream } from '@/utils/mobx-util'
import { withAbortSignal } from '@/utils/rx-util'
import { type Document } from './Document'
import { type DocumentStore } from './DocumentStore'
import { type UploadBarViewModel } from './UploadBarViewModel'
import type {
  JobDocumentListItemViewModel,
  JobDocumentListItemViewModelOptions,
} from './JobDocumentListItemViewModel'
import type { InternalCreateJobDocumentListItemViewModelFn } from './internal'

/**
 * Narrows the documents the server pages through.
 */
export interface JobDocumentListFilter {
  searchText?: string
  documentTypeId?: string
}

/**
 * Options for the {@link JobDocumentListViewModel}.
 */
export interface JobDocumentListViewModelOptions {
  /**
   * The job whose documents are listed.
   */
  jobId: Id

  /**
   * The workspace whose member permissions decide what the user may do.
   */
  workspaceId: Id

  /**
   * Tracks the uploads started from the list.
   */
  uploadBarVm: UploadBarViewModel

  /**
   * The current filter. A change refetches the documents.
   */
  filter: () => JobDocumentListFilter

  /**
   * Called when the user opens a document.
   */
  onView: (document: Document) => unknown
}

/**
 * View model for listing and selecting a job's documents.
 * The caller should call {@link fetch} when it wants the documents loaded,
 * as that isn't done on activate.
 */
export class JobDocumentListViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _documentVms: ViewModelList<
    Document,
    JobDocumentListItemViewModel
  >

  /**
   * The selected documents.
   */
  readonly selection = observable.set<Document>()

  readonly scrollProvider = new InfiniteScrollProvider<
    Document,
    JobDocumentListFilter
  >({
    limit: 30,
    fetchItems: ({ paging, signal, filter }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Loading documents failed',
        signal,
      )

      return withRetry(() =>
        this._documentStore.searchDocuments(
          {
            context: 'Job',
            jobId: this._options.jobId,
            searchText: filter?.searchText,
            documentTypeIds: filter?.documentTypeId
              ? [Id.assert(filter.documentTypeId)]
              : undefined,
            ...paging,
          },
          signal,
        ),
      )
    },
  })

  constructor(
    private readonly _options: JobDocumentListViewModelOptions,
    private readonly _documentStore: DocumentStore,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    createJobDocumentListItemViewModel: InternalCreateJobDocumentListItemViewModelFn,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    const itemOptions: JobDocumentListItemViewModelOptions = {
      workspaceId: this._options.workspaceId,
      selection: this.selection,
      uploadBarVm: this._options.uploadBarVm,
      jobId: this._options.jobId,
      getSignal: () => this._lifecycle.signal,
      onView: this._options.onView,
    }
    this._documentVms = new ViewModelList({
      models: () => this.documents,
      create: (document) =>
        createJobDocumentListItemViewModel(document, itemOptions),
    })

    this._lifecycle.manage(this._documentVms)
    this._lifecycle.manage(
      toStream(() => this._options.filter()).pipe(
        debounceTime(300),
        distinctUntilChanged<JobDocumentListFilter>(isEqual),
        switchMap((filter) =>
          withAbortSignal((signal) =>
            this.scrollProvider
              .fetch({ signal, filter, staleWhileRevalidate: true })
              .catch(() => undefined),
          ),
        ),
      ),
    )
  }

  /**
   * The loaded documents linked to the job, newest first.
   */
  @computed
  get documents() {
    return this._documentStore.forJob(this._options.jobId)
  }

  /**
   * View models for the documents.
   */
  @computed
  get documentVms() {
    return this._documentVms.items
  }

  @computed
  get canUploadDocs() {
    return this._getCurrentMember().permissions.docsUpload
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Loads the job's documents.
   */
  fetch = task(async (signal: AbortSignal) => {
    await this.scrollProvider.fetch({
      signal,
      filter: this._options.filter(),
    })
  })
}
